import { Injectable, inject } from "@angular/core";
import { ApiErrorResponse, ErrorStatusCodes } from "../models/api-response.model";
import { SnackbarService } from "src/app/shared/services/snackbar.service";
import { DatabaseTileService } from "src/app/shared/services/indexed-db/indexed-db-tile.service";
import { isString } from "lodash-es";

/**
 * Globally overrides the fetch function to intercept all network requests.
 * It detects HTTP status with custom codes in ErrorStatusCodes and handles them accordingly.
 * ErrorStatusCodes must be synced with ErrorStatusCodes sent by backend.
 *
 * Caches tile responses.
 *
 * @example
 * //Ensure this service is provided in root module
 * providers: [FetchInterceptorService]
 */
@Injectable({
  providedIn: "root",
})
export class FetchInterceptorService {
  private snackbarService = inject(SnackbarService);
  private databaseTileService = inject(DatabaseTileService);

  constructor() {
    this.overrideFetch();
  }

  private overrideFetch() {
    const originalFetch = window.fetch;

    window.fetch = async (...args) => {
      const url = args[0];
      try {
        let response = undefined;
        // Uncomment the following lines to enable tile caching.
        // Caches tile requests.
        // if (isString(url) && url.includes('tile')) {
        //   response = await this.handleTileRequest(originalFetch, url, args);
        // } else {
        //   response = await originalFetch(...args);
        //   await this.isResponseOk(response);
        // }
        response = await originalFetch(...args);
        await this.isResponseOk(response);
        return response;
      } catch (error: any) {
        console.debug("Global fetch error: ", error.message);
        return new Response(
          JSON.stringify({
            error: "Global fetch error",
            message: (error as any).message,
          }),
          { status: 500, headers: { "Content-Type": "application/json" } }
        );
      }
    };
  }

  private async isResponseOk(response: Response): Promise<boolean> {
    try {
      if (ErrorStatusCodes[response.status]) {
        const resObj = (await response.json()) as ApiErrorResponse;
        console.error(`${resObj.errorType}: ${resObj.details}`);
        this.snackbarService.error(resObj.message);
        return false;
      } else if (!response.ok) {
        throw new Error(`Request failed with status ${response.status}`);
      } else {
        return true;
      }
    } catch (error) {
      throw new Error(`Request failed with status ${response.status}`);
    }
  }

  private async handleTileRequest(
    originalFetch: typeof fetch,
    url: string,
    args: [input: RequestInfo | URL, init?: RequestInit | undefined]
  ): Promise<any> {
    let response = undefined;
    try {
      const cachedTile = await this.databaseTileService.getTile(url);
      if (cachedTile) {
        const reaableStream = new ReadableStream({
          start(controller) {
            controller.enqueue(new Uint8Array(cachedTile));
            controller.close();
          },
        });
        const cachedRes = new Response(reaableStream, {
          status: 200,
          headers: { "Content-Type": "application/x-protobuf" },
        });
        Object.defineProperty(cachedRes, "type", { value: "cors" });
        Object.defineProperty(cachedRes, "url", { value: url });

        return cachedRes;
      }
      response = await originalFetch(...args);
      const resStatusOk = await this.isResponseOk(response);
      if (resStatusOk) {
        const clonedRes = response.clone();
        const arrayBuffer = await clonedRes.arrayBuffer();
        await this.databaseTileService.saveTile(url, arrayBuffer);
        return response;
      } else {
        throw new Error(`Request failed with status ${response.status}`);
      }
    } catch (error: any) {
      if (error.message === "signal is aborted without reason") {
        console.debug("handleTileRequest cancelled as expected: ", error.message);
      } else {
        console.error("handleTileRequest error: ", error.message);
        throw error;
      }
    }
  }
}
